import { Link as RouterLink } from "react-router-dom";

import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import { useFlags } from "launchdarkly-react-client-sdk";

import { neutral } from "app/themes/colors";
import { PATIENT_PRONOUNS_FEATURE } from "constants/feature-flags";
import { BAS, PRONOUNS } from "constants/patients";
import useUserRoles from "hooks/use-user-roles.hook";
import type { Patient } from "services/types/patients.type";
import { formatBirthdateAsString } from "utils/common";

import PatientAssignedTags from "../../patient-assigned-tags";
import Avatar from "../../patient-header/components/avatar.component";
import BalanceDisplayCompact from "./balance-display-compact.component";
import TextCaption from "./text-caption.component";

interface PatientHeaderProps {
  patient: Patient;
}

export const PatientHeader: React.FC<PatientHeaderProps> = ({ patient }) => {
  const { isScheduler } = useUserRoles();
  const flags = useFlags();
  const pronounsFeatureEnabled = flags[PATIENT_PRONOUNS_FEATURE];
  const ageAndSexField = [
    patient.age_years ? `${patient.age_years} years` : "",
    patient.sex ? BAS.getFromCode(patient.sex) : ""
  ]
    .filter(Boolean)
    .join(", ");

  const pronounsLabel =
    pronounsFeatureEnabled && patient.pronouns
      ? PRONOUNS.getFromCode(patient.pronouns)
      : "";

  return (
    <Stack sx={{ pt: 0 }}>
      <Box display="flex">
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap={0.5}
        >
          <Avatar
            id={patient.id}
            fullName={patient.fullName!}
            avatar={patient.avatar!}
            imgSize={100}
            expandPhoto={false}
          />
          <Stack>
            {patient.created_at_display && (
              <Tooltip
                title={`Created By: ${
                  patient.created_by
                    ? patient.created_by.fullName ||
                      `${patient.created_by.first_name} ${patient.created_by.last_name}`
                    : "N/A"
                }`}
                placement="bottom"
              >
                <Typography variant="caption" textAlign="center">
                  Patient Added: {patient.created_at_display}
                </Typography>
              </Tooltip>
            )}

            {Boolean(patient?.suno_comms_id) && (
              <Link
                component="a"
                href={`/leads/list/${patient?.suno_comms_id}`}
                target="_blank"
                rel="noopener noreferrer"
                variant="caption"
                underline="hover"
              >
                Converted from Lead
              </Link>
            )}
          </Stack>
        </Box>
        <Box marginLeft={2}>
          <Typography variant="h5" sx={{ mb: 0.5 }}>
            <Link component={RouterLink} to={`/patients/${patient?.id}/`}>
              {patient.fullName}
            </Link>
          </Typography>
          <Typography variant="body2" color={neutral[800]}>
            {ageAndSexField}
          </Typography>
          {pronounsLabel && (
            <Typography variant="body2" color={neutral[800]}>
              <TextCaption>Pronouns:</TextCaption> {pronounsLabel}
            </Typography>
          )}
          <Typography variant="body2" color={neutral[800]}>
            {Boolean(patient.birthdate) &&
              formatBirthdateAsString(patient.birthdate)}
          </Typography>
          <Stack direction="row" justifyContent="space-between">
            <Stack sx={{ mt: 0.5 }}>
              <TextCaption>Suno ID:</TextCaption> {patient.id}
              {patient.noah_id && (
                <>
                  {" "}
                  <TextCaption>Noah Patient ID:</TextCaption>{" "}
                  {String(patient.noah_id).padStart(7, "0")}
                </>
              )}
            </Stack>

            {!isScheduler && (
              <BalanceDisplayCompact
                patient={patient}
                totalBalance={patient.totalBalance}
                insurerResponsibilityBalance={Number(
                  patient.insurer_responsibility_balance || "0"
                )}
                patientResponsibilityBalance={Number(
                  patient.patient_responsibility_balance || "0"
                )}
                managedCareResponsibilityBalance={Number(
                  patient.managed_care_responsibility_balance || "0"
                )}
                managedCarePlan={patient.managed_care_plan ?? undefined}
                insurancePolicy={patient.primary_insurance_policy}
                paymentSourceType={Number(patient.payment_source_type)}
              />
            )}
          </Stack>
        </Box>
      </Box>
      <PatientAssignedTags patientId={patient.id} sx={{ mt: 1, mb: 1 }} />
    </Stack>
  );
};
